import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ACTING_KEY = 'qimam_acting_branch_slug';

// One-time install: axios interceptor that injects X-Acting-Branch from localStorage.
// Allows super_admin to act as a specific branch across all axios calls.
axios.interceptors.request.use((config) => {
  const acting = localStorage.getItem(ACTING_KEY);
  if (acting) {
    config.headers['X-Acting-Branch'] = acting;
  }
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [branch, setBranch] = useState(null);
  const [actingBranch, setActingBranchState] = useState(() => localStorage.getItem(ACTING_KEY));
  const [loading, setLoading] = useState(true);

  const setActingBranch = useCallback((slug) => {
    if (slug) {
      localStorage.setItem(ACTING_KEY, slug);
    } else {
      localStorage.removeItem(ACTING_KEY);
    }
    setActingBranchState(slug || null);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);

      if (response.data.role === 'employee' && response.data.employee_id) {
        const empResponse = await axios.get(`${API_URL}/api/employees`);
        const emp = empResponse.data.find(e => e.id === response.data.employee_id);
        setEmployee(emp);
      }

      // Resolve branch:
      // - super_admin acting as a branch → fetch by slug
      // - super_admin NOT acting → no branch (sees network)
      // - everyone else → their branch_id
      const acting = localStorage.getItem(ACTING_KEY);
      if (response.data.role === 'super_admin') {
        if (acting) {
          try {
            const r = await axios.get(`${API_URL}/api/branches/by-slug/${acting}`);
            setBranch(r.data);
          } catch (_e) {
            localStorage.removeItem(ACTING_KEY);
            setActingBranchState(null);
            setBranch(null);
          }
        } else {
          setBranch(null);
        }
      } else if (response.data.branch_id) {
        const branchResponse = await axios.get(`${API_URL}/api/branches`);
        const branchData = branchResponse.data.find(b => b.id === response.data.branch_id) || branchResponse.data[0];
        setBranch(branchData);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (phone, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { phone, password });
    const { access_token, user: userData, employee: employeeData, branch: branchData } = response.data;

    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

    setUser(userData);
    setEmployee(employeeData);
    setBranch(branchData);

    return { user: userData, employee: employeeData, branch: branchData };
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem(ACTING_KEY);
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setEmployee(null);
    setBranch(null);
    setActingBranchState(null);
  };

  const value = {
    user,
    employee,
    branch,
    loading,
    actingBranch,
    setActingBranch,
    login,
    logout,
    refreshUser: fetchCurrentUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
